import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Send } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/AdminShell";
import { LoadingState } from "../../components/LoadingState";
import { ActionButton, CustomerInput, CustomerPanel, CustomerSelect, StatusBadge } from "../../components/customer/CustomerUI";
import api, { formatApiError } from "../../lib/api";

const STATUSES = ["open", "in_progress", "waiting_customer", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

function date(value) {
  return value ? new Date(value).toLocaleString() : "—";
}

export default function SupportCenter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTicket = searchParams.get("ticket");
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [filters, setFilters] = useState({ q: "", status: "", priority: "" });
  const [reply, setReply] = useState("");

  const loadTicket = useCallback(async (ticketId) => {
    if (!ticketId) return;
    const { data } = await api.get(`/admin/support/tickets/${ticketId}`);
    setSelected(data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const { data } = await api.get("/admin/support/tickets", { params });
      setTickets(data.items || []);
      const ticketId = requestedTicket || selected?.id;
      if (ticketId) {
        await loadTicket(ticketId);
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setLoading(false); }
  }, [filters, loadTicket, requestedTicket, selected?.id]);

  useEffect(() => { load(); }, [load]);

  const update = async (values) => {
    if (!selected) return;
    setBusy("update");
    try {
      await api.patch(`/admin/support/tickets/${selected.id}`, values);
      await Promise.all([loadTicket(selected.id), load()]);
      toast.success("Support ticket updated.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selected || !reply.trim()) return;
    setBusy("reply");
    try {
      await api.post(`/admin/support/tickets/${selected.id}/messages`, { body: reply });
      setReply("");
      await Promise.all([loadTicket(selected.id), load()]);
      toast.success("Customer notified of the secure in-app reply.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return <AdminShell title="Support Center" subtitle="Account-linked complaints, investigation context, and customer responses">
    <div className="space-y-5" data-testid="admin-support-page">
      <CustomerPanel>
      <div className="grid gap-3 px-ui-4 py-ui-4 sm:px-ui-5 md:grid-cols-[1fr_190px_190px_auto]">
        <label className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-600" />
          <CustomerInput value={filters.q} onChange={(e) => setFilters({...filters, q:e.target.value})}
            placeholder="Reference, email, subject, CRM or scan"
            className="pl-10" />
        </label>
        <CustomerSelect value={filters.status} onChange={(e) => setFilters({...filters, status:e.target.value})}>
          <option value="">All statuses</option>
          {STATUSES.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
        </CustomerSelect>
        <CustomerSelect value={filters.priority} onChange={(e) => setFilters({...filters, priority:e.target.value})}>
          <option value="">All priorities</option>
          {PRIORITIES.map((value) => <option key={value} value={value}>{value}</option>)}
        </CustomerSelect>
        <ActionButton onClick={load}>Refresh</ActionButton>
      </div>
      </CustomerPanel>

      <div className="grid gap-5 xl:grid-cols-[420px_1fr]">
        <CustomerPanel title={`Support queue · ${tickets.length}`}>
          <div className="px-ui-4 py-ui-4 sm:px-ui-5">
          {loading ? <LoadingState label="Loading support queue" /> :
            <div className="max-h-[720px] divide-y divide-slate-100 overflow-auto">
              {tickets.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No matching support tickets.</div>}
              {tickets.map((ticket) => <button key={ticket.id}
                data-testid={`admin-support-ticket-${ticket.id}`} onClick={() => {
                  setSearchParams({ ticket: ticket.id });
                  loadTicket(ticket.id);
                }}
                className={"w-full p-4 text-left hover:bg-slate-50 " + (selected?.id === ticket.id ? "bg-blue-50" : "")}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] font-semibold text-blue-600">{ticket.reference}</span>
                  <StatusBadge tone={["high", "urgent"].includes(ticket.priority) ? "danger" : ticket.priority === "normal" ? "info" : "neutral"}>{ticket.priority}</StatusBadge>
                </div>
                <div className="mt-2 truncate text-sm font-semibold text-slate-900">{ticket.subject}</div>
                <div className="mt-1 truncate text-xs text-slate-500">{ticket.created_by_email}</div>
                <div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-slate-600"><StatusBadge tone={["resolved", "closed"].includes(ticket.status) ? "success" : ticket.status === "waiting_customer" ? "warning" : "info"}>{ticket.status.replaceAll("_", " ")}</StatusBadge><span>{date(ticket.updated_at)}</span></div>
              </button>)}
            </div>}
          </div>
        </CustomerPanel>

        <CustomerPanel className="min-h-[620px]">
          <div className="px-ui-4 py-ui-4 sm:px-ui-5">
          {!selected ? <div className="flex min-h-[620px] items-center justify-center text-sm text-slate-500">Select a support ticket to investigate.</div> :
            <div data-testid="admin-support-detail">
              <div className="border-b border-slate-200 p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div><div className="font-mono text-xs text-blue-600">{selected.reference}</div><h2 className="mt-1 font-heading text-2xl font-semibold">{selected.subject}</h2><p className="mt-1 text-sm text-slate-500">{selected.created_by_name} · {selected.created_by_email}</p></div>
                  <div className="flex gap-2">
                    <CustomerSelect value={selected.priority} disabled={busy === "update"}
                      onChange={(e) => update({ priority:e.target.value })}
                      className="w-auto text-xs capitalize">
                      {PRIORITIES.map((value) => <option key={value}>{value}</option>)}
                    </CustomerSelect>
                    <CustomerSelect value={selected.status} disabled={busy === "update"}
                      onChange={(e) => update({ status:e.target.value })}
                      className="w-auto text-xs capitalize">
                      {STATUSES.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
                    </CustomerSelect>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 text-xs sm:grid-cols-3">
                  <div className="rounded-xl bg-slate-50 p-3"><div className="text-slate-600">Organization</div><div className="mt-1 font-semibold">{selected.context?.organization?.name}</div><div className="mt-1 break-all font-mono text-[10px] text-slate-600">{selected.organization_id}</div></div>
                  <div className="rounded-xl bg-slate-50 p-3"><div className="text-slate-600">CRM</div><div className="mt-1 font-semibold">{selected.context?.connection?.label || "Not attached"}</div><div className="mt-1 text-slate-500">{selected.context?.connection?.connector_name}</div></div>
                  <div className="rounded-xl bg-slate-50 p-3"><div className="text-slate-600">Scan</div><div className="mt-1 break-all font-mono text-[10px]">{selected.scan_id || "Not attached"}</div><div className="mt-1 text-slate-500">{selected.context?.scan ? `${selected.context.scan.status} · score ${selected.context.scan.health_score ?? "—"}` : ""}</div></div>
                </div>
              </div>
              <div className="max-h-[420px] space-y-3 overflow-auto bg-slate-50 p-5">
                {(selected.messages || []).map((message) => <article key={message.id}
                  className={"max-w-3xl rounded-xl border p-4 " + (message.author_role === "support" ? "border-blue-200 bg-blue-50" : "ml-auto border-slate-200 bg-white")}>
                  <div className="flex justify-between gap-3 text-xs"><span className="font-semibold">{message.author_role === "support" ? "CleanIQ Support" : message.author_name}</span><span className="text-slate-600">{date(message.created_at)}</span></div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{message.body}</p>
                </article>)}
              </div>
              <form onSubmit={sendReply} className="border-t border-slate-200 p-4">
                <textarea data-testid="admin-support-reply" required maxLength={5000} rows={4}
                  value={reply} onChange={(e) => setReply(e.target.value)}
                  placeholder="Reply to the customer. The message remains inside their authenticated CleanIQ workspace."
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm" />
                <ActionButton type="submit" data-testid="admin-support-reply-submit" disabled={busy === "reply"} className="mt-3">
                  <Send className="h-4 w-4" /> {busy === "reply" ? "Sending…" : "Send secure reply"}
                </ActionButton>
              </form>
            </div>}
          </div>
        </CustomerPanel>
      </div>
    </div>
  </AdminShell>;
}
